import { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import { EventLogEntry, EventLogFilters } from '../types';
import { getEventLogs } from '../api-integration-requests';

const DEFAULT_SIZE = 25;

const STATUS_LEVEL_MAP: Record<string, { label: string; bg: string; color: string }> = {
  '2': { label: 'Success', bg: '#e8f5e9', color: '#2e7d32' },
  '3': { label: 'Info',    bg: '#e8f4fd', color: '#1565c0' },
  '4': { label: 'Warning', bg: '#fff3e0', color: '#e65100' },
  '5': { label: 'Error',   bg: '#fdecea', color: '#c62828' },
};

export { DEFAULT_SIZE, STATUS_LEVEL_MAP };

const defaultFilters = (): EventLogFilters => ({
  thirdPartyId: '',
  operationId: '',
  startDate: moment().subtract(7, 'days').format('MM/DD/YYYY'),
  endDate: moment().format('MM/DD/YYYY'),
  size: DEFAULT_SIZE,
});

export const useEventLogs = () => {
  const [logs, setLogs] = useState<EventLogEntry[]>([]);
  const [expandedTraceId, setExpandedTraceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [filters, setFilters] = useState<EventLogFilters>(defaultFilters);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.thirdPartyId) count++;
    if (filters.operationId) count++;
    if (filters.startDate !== moment().subtract(7, 'days').format('MM/DD/YYYY')) count++;
    if (filters.endDate !== moment().format('MM/DD/YYYY')) count++;
    if (filters.size !== DEFAULT_SIZE) count++;
    if (levelFilter) count++;
    return count;
  }, [filters, levelFilter]);

  const filteredLogs = useMemo(() => {
    let result = logs;
    if (levelFilter) {
      result = result.filter(log => {
        const firstDigit = String(log.responseStatus).charAt(0);
        return STATUS_LEVEL_MAP[firstDigit]?.label.toLowerCase() === levelFilter;
      });
    }
    if (!keyword.trim()) return result;
    const lower = keyword.trim().toLowerCase();
    return result.filter(log =>
      [log.path, log.operationId, log.traceId, log.queryString, log.requestBody, log.responseBody, log.clientIp]
        .some(field => field?.toLowerCase().includes(lower))
    );
  }, [logs, keyword, levelFilter]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getEventLogs({
        thirdPartyId: filters.thirdPartyId || undefined,
        operationId: filters.operationId || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        size: filters.size,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setLogs(response.body || []);
    } catch (err) {
      console.error('Failed to fetch event logs:', err);
      setError('Failed to load event logs. Please try again.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (field: keyof EventLogFilters) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'size' ? Number(event.target.value) : event.target.value;
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (field: 'startDate' | 'endDate') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilters(prev => ({ ...prev, [field]: fromInputDate(event.target.value) }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters());
    setLevelFilter('');
  };

  return {
    logs,
    filteredLogs,
    loading,
    error,
    filters,
    showFilters,
    keyword,
    levelFilter,
    activeFilterCount,
    expandedTraceId,
    setExpandedTraceId,
    setShowFilters,
    setKeyword,
    setLevelFilter,
    handleFilterChange,
    handleDateChange,
    resetFilters,
    fetchLogs,
  };
};

// ── Date helpers (shared with filter panel) ────────────────────────────────
export const toInputDate = (mmddyyyy: string): string => {
  if (!mmddyyyy) return '';
  const m = moment(mmddyyyy, 'MM/DD/YYYY');
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

const fromInputDate = (isoValue: string): string =>
  isoValue ? moment(isoValue).format('MM/DD/YYYY') : '';
